import React from "react";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Info } from "lucide-react";
import { AssessmentResultType } from "../../pages/Assessment";
import { Day } from "react-day-picker";

const questions = [
  {
    id: 1,
    text: "Little interest or pleasure in doing things",
    source: "PHQ-9"
  },
  {
    id: 2,
    text: "Feeling down, depressed, or hopeless",
    source: "PHQ-9"
  },
  {
    id: 3,
    text: "Trouble falling or staying asleep, or sleeping too much",
    source: "PHQ-9"
  },
  {
    id: 4,
    text: "Feeling tired or having little energy",
    source: "PHQ-9"
  },
  {
    id: 5,
    text: "Poor appetite or overeating",
    source: "PHQ-9"
  },
  {
    id: 6,
    text: "Feeling nervous, anxious, or on edge",
    source: "GAD-7"
  },
  {
    id: 7,
    text: "Not being able to stop or control worrying",
    source: "GAD-7"
  },
  {
    id: 8,
    text: "Worrying too much about different things",
    source: "GAD-7"
  },
  {
    id: 9,
    text: "Trouble relaxing",
    source: "GAD-7"
  },
  {
    id: 10,
    text: "Being so restless that it is hard to sit still",
    source: "GAD-7"
  }
];

const options = [
  { value: "0", label: "Not at all", description: "No days" },
  { value: "1", label: "Several days", description: "Less than half the days" },
  { value: "2", label: "More than half the days", description: "" },
  { value: "3", label: "Nearly every day", description: "" }
];

type MentalHealthQuizProps = {
  onComplete: (result: AssessmentResultType) => void;
};

const MentalHealthQuiz = ({ onComplete }: MentalHealthQuizProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleNext = () => {
    if (selectedOption !== null) {
      setAnswers({ ...answers, [questions[currentQuestion].id]: selectedOption });
      setSelectedOption(null);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Quiz completed, calculate results
        const result = calculateResult();
        onComplete(result);
      }
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      const previousAnswer = answers[questions[currentQuestion - 1].id];
      setSelectedOption(previousAnswer);
    }
  };

  const calculateResult = (): AssessmentResultType => {
    const totalScore = Object.values(answers).reduce(
      (sum, value) => sum + parseInt(value, 10),
      0
    );
    
    // Determine severity based on the total score (based on PHQ-9 & GAD-7 scales)
    let severityLevel: AssessmentResultType["severityLevel"];
    let recommendation = "";
    
    if (totalScore <= 4) {
      severityLevel = "minimal";
      recommendation = "Your responses suggest minimal levels of distress. Continue with self-care practices and reach out if symptoms change.";
    } else if (totalScore <= 9) {
      severityLevel = "mild";
      recommendation = "Your responses suggest mild levels of distress. Consider some self-help strategies and monitoring your symptoms.";
    } else if (totalScore <= 14) {
      severityLevel = "moderate";
      recommendation = "Your responses suggest moderate levels of distress. It may be beneficial to speak with a mental health professional.";
    } else if (totalScore <= 19) {
      severityLevel = "moderately severe";
      recommendation = "Your responses suggest moderately severe levels of distress. We recommend connecting with a therapist to discuss support options.";
    } else {
      severityLevel = "severe";
      recommendation = "Your responses suggest severe levels of distress. We strongly recommend connecting with a mental health professional soon.";
    }
    
    return {
      score: totalScore,
      severityLevel,
      recommendation
    };
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentQ = questions[currentQuestion];

  return (
    <div className="space-y-8">
      <div className="space-y-4 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Mental Health Assessment</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Over the last 2 weeks, how often have you been bothered by the following problems?
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>This screening is not a diagnostic tool</AlertTitle>
        <AlertDescription>
          This assessment is based on standardized screening tools (PHQ-9 and GAD-7) and is meant to help identify potential concerns. It is not a substitute for professional evaluation.
        </AlertDescription>
      </Alert>

      <Card className="border-2 border-violet-100">
        <CardHeader>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div 
              className="bg-violet-600 h-2.5 rounded-full" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <CardTitle className="text-xl">Question {currentQuestion + 1} of {questions.length}</CardTitle>
          <CardDescription>
            {currentQ.source === "PHQ-9" ? "Depression Screening" : "Anxiety Screening"}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">{currentQ.text}</h3>
            <RadioGroup 
              value={selectedOption || ""} 
              onValueChange={setSelectedOption}
              className="space-y-3"
            >
              {options.map((option) => (
                <div key={option.value} className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-violet-50 transition-colors">
                  <RadioGroupItem value={option.value} id={`option-${option.value}`} />
                  <Label htmlFor={`option-${option.value}`} className="flex-1 cursor-pointer">
                    <div className="font-medium">{option.label}</div>
                    {option.description && (
                      <div className="text-sm text-gray-500">{option.description}</div>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between pt-4">
          <Button 
            variant="outline" 
            onClick={handleBack} 
            disabled={currentQuestion === 0}
          >
            Back
          </Button>
          <Button 
            onClick={handleNext} 
            disabled={selectedOption === null}
          >
            {currentQuestion < questions.length - 1 ? "Next" : "Complete Assessment"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default MentalHealthQuiz;
